import { BRAND_GREEN } from "./brandColors";

/** Original bright palette — used to migrate stored member colours. */
export const LEGACY_MEMBER_COLORS = [
    "#00FFA3",
    "#00D4FF",
    "#B026FF",
    "#FF5C00",
    "#FFD23F",
    "#FF4D8D",
    "#7CFFB2",
    "#9D8DFF",
];

/** Default split-wheel palette — ~25% darker (green matches brand accent). */
export const DEFAULT_MEMBER_COLORS = [
    "#00BF0D",
    "#009FBF",
    "#941FD9",
    "#BF4500",
    "#BF9E2F",
    "#BF3A6A",
    "#5DBF85",
    "#766ABF",
];

export const LEGACY_MEMBER_COLOR_MAP = Object.fromEntries(
    LEGACY_MEMBER_COLORS.map((legacy, index) => [
        legacy.toUpperCase(),
        DEFAULT_MEMBER_COLORS[index],
    ]),
);

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/** Minimum relative luminance — blocks black/near-black; white and bright colours pass. */
const MIN_MEMBER_COLOR_LUMINANCE = 0.12;

function hexToRgb(hex) {
    const n = parseInt(hex.slice(1), 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function relativeLuminance(hex) {
    const { r, g, b } = hexToRgb(hex);
    const linear = [r, g, b].map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

export function defaultMemberColor(index = 0) {
    return DEFAULT_MEMBER_COLORS[index % DEFAULT_MEMBER_COLORS.length];
}

export function normalizeHexColor(value, fallback = BRAND_GREEN) {
    if (typeof value !== "string") return fallback;
    const trimmed = value.trim();
    if (!trimmed.startsWith("#") || trimmed.length !== 7) return fallback;
    return HEX_RE.test(trimmed) ? trimmed.toUpperCase() : fallback;
}

/** Map legacy palette swatches to the darker defaults; leave custom colours unchanged. */
export function resolveMemberColor(value, fallback = BRAND_GREEN) {
    const normalized = normalizeHexColor(value, fallback);
    return LEGACY_MEMBER_COLOR_MAP[normalized] || normalized;
}

export function isAllowedMemberColor(value) {
    if (typeof value !== "string") return false;
    const trimmed = value.trim();
    if (!trimmed.startsWith("#") || trimmed.length !== 7 || !HEX_RE.test(trimmed)) return false;
    return relativeLuminance(trimmed.toUpperCase()) >= MIN_MEMBER_COLOR_LUMINANCE;
}

export function sanitizeMemberColor(value, fallback = BRAND_GREEN) {
    const normalized = normalizeHexColor(value, fallback);
    const resolved = resolveMemberColor(normalized, fallback);
    return isAllowedMemberColor(resolved) ? resolved : normalizeHexColor(fallback, BRAND_GREEN);
}
